/**
 * Availability.js — "Check Availability" page
 *
 * Reached from the "Choose Your Schedule" step in <Works />, and bookable
 * directly at its own route. Lets a visitor pick a date + time and submit
 * their details to request a session slot.
 *
 * Behavior:
 *  - Mobile: a 3-step accordion (Date → Time → Details). Picking a date
 *    auto-advances to Time; picking a time auto-advances to Details.
 *    Completed steps collapse to a summary chip with an "Edit" link.
 *  - Desktop (>=64em): all three cards render expanded side by side, plus
 *    a "Your Selection" summary panel — no accordion behavior, driven
 *    entirely by CSS in Availability.styles.js so this file doesn't need
 *    separate desktop/mobile branches.
 *
 * Mirrors the Welcome.js / Works.js module conventions:
 *  ✓ AvailabilityPageMeta     — title/description/OG/canonical/JSON-LD
 *  ✓ useAvailabilityAnalytics — page view, date/time select, submit events
 *  ✓ Styled-components imported from ./Availability.styles
 */

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AvailabilityPageMeta } from "./AvailabilitySeoMeta";
import useAvailabilityAnalytics from "./useAvailabilityAnalytics";
import { submitAvailabilityRequest } from "./availabilityApi";
import {
  Section, Inner,
  HeaderWrap, TopIconWrap, Title, Subtitle,
  TrustRow, TrustItem, TrustDot,
  GridWrap,
  Card, CardHeader, CardHeaderIcon, CardHeaderTitle, CardHeaderRight,
  Chevron, EditBtn, SummaryRow, SummaryValue, CardBody,
  CalendarNav, CalendarNavBtn, CalendarLabel,
  WeekRow, WeekdayLabel, DaysGrid, DayCell, CalendarLegend, LegendItem,
  DateChip, TimeGroup, TimeGroupLabel, TimeSlotsGrid, TimeSlot, TimezoneNote,
  CardSubtitle, FormGrid, Field, Label, InputWrap, CountryCode, Input,
  ErrorText, PrivacyNote, SubmitRow, SubmitBtn, SubmitHint, FormStatus,
  AsideCard, AsideHeading, AsideRows, AsideRow, AsideRowLabel, AsideRowValue,
  SessionIcon, SessionTitle, SessionDesc, StepCaption,
  FeatureGrid, FeatureItem, FeatureIcon, FeatureTitle, FeatureDesc, NoteBar,
} from "./Availability.styles";

// ═══════════════════════════════════════════════════════════════════════════════
// ICONS — small inline SVGs, no external icon-library dependency.
// ═══════════════════════════════════════════════════════════════════════════════

const iconBase = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: false,
};

const IconCalendar = (p) => (
  <svg {...iconBase} {...p}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
    <path d="M3.5 9.5h17" />
    <path d="M8 3v3.5M16 3v3.5" />
  </svg>
);
const IconClock = (p) => (
  <svg {...iconBase} {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5V12l3.2 1.8" />
  </svg>
);
const IconUser = (p) => (
  <svg {...iconBase} {...p}>
    <circle cx="12" cy="8.2" r="3.6" />
    <path d="M4.5 19.5c0-4 3.4-6 7.5-6s7.5 2 7.5 6" />
  </svg>
);
const IconCheck = (p) => (
  <svg {...iconBase} {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.2 12.3l2.6 2.6 5-5.3" />
  </svg>
);
const IconShield = (p) => (
  <svg {...iconBase} {...p}>
    <path d="M12 3.2l6.5 2.6v5.4c0 4.4-2.8 7.6-6.5 8.6-3.7-1-6.5-4.2-6.5-8.6V5.8L12 3.2z" />
    <path d="M9 12l2 2 4-4.2" />
  </svg>
);
const IconBell = (p) => (
  <svg {...iconBase} {...p}>
    <path d="M6 9.5a6 6 0 0112 0c0 4 1.4 5.5 1.4 5.5H4.6S6 13.5 6 9.5z" />
    <path d="M10.2 18.5a1.9 1.9 0 003.6 0" />
  </svg>
);
const IconHeart = (p) => (
  <svg {...iconBase} {...p}>
    <path d="M12 20.2c-4.8-3-8.4-6.4-8.4-10.2A4.6 4.6 0 0112 6.8 4.6 4.6 0 0120.4 10c0 3.8-3.6 7.2-8.4 10.2z" />
  </svg>
);
const IconSun = (p) => (
  <svg {...iconBase} {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 3v2M12 19v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M3 12h2M19 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
  </svg>
);
const IconMoon = (p) => (
  <svg {...iconBase} {...p}>
    <path d="M20 14.5A8.5 8.5 0 019.5 4a8.5 8.5 0 1010.5 10.5z" />
  </svg>
);
const IconMail = (p) => (
  <svg {...iconBase} {...p}>
    <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
    <path d="M4 6.5l8 6.5 8-6.5" />
  </svg>
);
const IconPhone = (p) => (
  <svg {...iconBase} {...p}>
    <path d="M6 3.5h3l1.4 4.5-2 1.6a12.5 12.5 0 006 6l1.6-2 4.5 1.4v3a1.8 1.8 0 01-2 1.8A16.5 16.5 0 014.2 5.5a1.8 1.8 0 011.8-2z" />
  </svg>
);
const IconLock = (p) => (
  <svg {...iconBase} {...p}>
    <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
    <path d="M8 10.5V7.5a4 4 0 018 0v3" />
  </svg>
);
const IconLeaf = (p) => (
  <svg {...iconBase} {...p}>
    <path d="M5 20c0-8.5 5-15 14-15 0 9.5-6.5 15-14 15z" />
    <path d="M5.5 19.5C8 16 11 12.5 14.5 8.5" />
  </svg>
);
const IconArrowRight = (p) => (
  <svg {...iconBase} {...p}>
    <path d="M4 12h15.2" />
    <path d="M13.5 5.5L20 12l-6.5 6.5" />
  </svg>
);
const IconChevronLeft = (p) => (
  <svg {...iconBase} {...p}>
    <polyline points="15 5 9 12 15 19" />
  </svg>
);
const IconChevronRight = (p) => (
  <svg {...iconBase} {...p}>
    <polyline points="9 5 15 12 9 19" />
  </svg>
);
const IconLotus = (p) => (
  <svg {...iconBase} {...p}>
    <circle cx="12" cy="6.4" r="2.1" />
    <path d="M12 8.7v3.6" />
    <path d="M12 12.3c-2.8.8-4.8 2.6-5.6 4.6" />
    <path d="M12 12.3c2.8.8 4.8 2.6 5.6 4.6" />
    <path d="M9.3 15.4c-1.7.7-2.7.9-2.7.9" />
    <path d="M14.7 15.4c1.7.7 2.7.9 2.7.9" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Content data
// ═══════════════════════════════════════════════════════════════════════════════

const TRUST_ITEMS = [
  { icon: IconCheck, label: "Free to check" },
  { icon: IconClock, label: "Takes less than a minute" },
  { icon: IconShield, label: "No payment required" },
];

const FEATURES = [
  { icon: IconCalendar, title: "Easy Scheduling", desc: "Pick a date and time that works for you." },
  { icon: IconShield,   title: "Secure & Private", desc: "Your details are 100% safe with us." },
  { icon: IconBell,     title: "Instant Confirmation", desc: "Receive confirmation on your mobile." },
  { icon: IconHeart,    title: "For Your Well-being", desc: "Personalized yoga just for you." },
];

const TIME_GROUPS = [
  { label: "Morning", icon: IconSun, slots: ["09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM"] },
  { label: "Afternoon", icon: IconSun, slots: ["02:00 PM", "02:30 PM", "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM"] },
  { label: "Evening", icon: IconMoon, slots: ["05:00 PM", "05:30 PM", "06:00 PM", "06:30 PM", "07:00 PM"] },
];

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const STEP_LABELS = {
  1: "Select Date",
  2: "Select Time",
  3: "Enter Details & Confirm",
};

// ─── Date helpers (no external date library) ───────────────────────────────────

const sameDay = (a, b) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const isPastDay = (date, today) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return d < t;
};

const buildMonthMatrix = (year, month) => {
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = (firstOfMonth.getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = startOffset; i > 0; i--) {
    cells.push({ date: new Date(year, month - 1, daysInPrevMonth - i + 1), inMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), inMonth: true });
  }
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].date;
    const next = new Date(last);
    next.setDate(last.getDate() + 1);
    cells.push({ date: next, inMonth: false });
  }
  return cells;
};

const formatMonthLabel = (date) =>
  new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(date);

const formatDateLong = (date) =>
  new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(date);

const toISODate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

// ═══════════════════════════════════════════════════════════════════════════════

const DEFAULT_NEXT_PATH = "/contactUs";

const Availability = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const analytics = useAvailabilityAnalytics();

  const today = useMemo(() => new Date(), []);

  // ── Calendar / selection state ─────────────────────────────────────────────
  const [viewMonth, setViewMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  // ── Mobile accordion state ───────────────────────────────────────────────────
  const [openStep, setOpenStep] = useState(1);

  // ── Form state ────────────────────────────────────────────────────────────────
  const [form, setForm] = useState({ name: "", countryCode: "+91", mobile: "", email: "" });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // { variant: 'success' | 'error', message }

  useEffect(() => {
    analytics.trackPageView(location.state?.fromStepName);
    // Fire once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const monthMatrix = useMemo(
    () => buildMonthMatrix(viewMonth.getFullYear(), viewMonth.getMonth()),
    [viewMonth]
  );

  const canGoPrevMonth =
    viewMonth.getFullYear() > today.getFullYear() ||
    (viewMonth.getFullYear() === today.getFullYear() && viewMonth.getMonth() > today.getMonth());

  const handlePrevMonth = useCallback(() => {
    if (!canGoPrevMonth) return;
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  }, [canGoPrevMonth]);

  const handleNextMonth = useCallback(() => {
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  }, []);

  const handleSelectDate = useCallback((date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setOpenStep(2);
    analytics.trackDateSelect(toISODate(date));
  }, [analytics]);

  const handleSelectTime = useCallback((time) => {
    setSelectedTime(time);
    setOpenStep(3);
    analytics.trackTimeSelect(time);
  }, [analytics]);

  const handleHeaderClick = useCallback((step, reachable) => {
    if (!reachable) return;
    setOpenStep(step);
  }, []);

  const updateField = useCallback((field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setFormErrors((e) => ({ ...e, [field]: undefined }));
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Please enter your full name";
    if (!/^[0-9]{10}$/.test(form.mobile.trim())) errors.mobile = "Enter a valid 10-digit mobile number";
    if (form.email.trim() && !isValidEmail(form.email.trim())) errors.email = "Enter a valid email address";
    if (!selectedDate) errors.date = "Please select a date";
    if (!selectedTime) errors.time = "Please select a time";
    setFormErrors(errors);
    return errors;
  }, [form, selectedDate, selectedTime]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (errors.date) { setOpenStep(1); return; }
    if (errors.time) { setOpenStep(2); return; }
    if (Object.keys(errors).length > 0) { setOpenStep(3); return; }

    setSubmitting(true);
    setStatus(null);
    try {
      await submitAvailabilityRequest({
        name: form.name.trim(),
        mobile: form.mobile.trim(),
        countryCode: form.countryCode,
        email: form.email.trim(),
        date: toISODate(selectedDate),
        time: selectedTime,
        source: "availability_page",
      });
      setStatus({ variant: "success", message: "You're all set! We'll confirm your session on your mobile number shortly." });
      analytics.trackFormSubmit({ status: "success", date: toISODate(selectedDate), time: selectedTime });
    } catch (err) {
      setStatus({ variant: "error", message: err?.message || "Something went wrong. Please try again." });
      analytics.trackFormSubmit({ status: "error", date: toISODate(selectedDate), time: selectedTime });
    } finally {
      setSubmitting(false);
    }
  }, [form, selectedDate, selectedTime, validateForm, analytics]);

  const dateReachable = true;
  const timeReachable = !!selectedDate;
  const detailsReachable = !!selectedTime;

  const dateStatus = openStep === 1 ? "open" : selectedDate ? "done" : "pending";
  const timeStatus = openStep === 2 ? "open" : selectedTime ? "done" : "pending";
  const detailsStatus = openStep === 3 ? "open" : "pending";

  return (
    <Section>
      <AvailabilityPageMeta />

      <Inner>
        <HeaderWrap>
          <TopIconWrap><IconCalendar /></TopIconWrap>
          <Title>Check Availability</Title>
          <Subtitle>Choose a convenient date and time for your Yoga Shastra session.</Subtitle>
        </HeaderWrap>

        <TrustRow>
          {TRUST_ITEMS.map(({ icon: Icon, label }, i) => (
            <React.Fragment key={label}>
              <TrustItem><Icon /><span>{label}</span></TrustItem>
              {i < TRUST_ITEMS.length - 1 && <TrustDot>•</TrustDot>}
            </React.Fragment>
          ))}
        </TrustRow>

        <GridWrap>
          {/* ── 1. Select a Date ── */}
          <Card $area="date">
            <CardHeader
              type="button"
              onClick={() => handleHeaderClick(1, dateReachable)}
              aria-expanded={dateStatus === "open"}
            >
              <CardHeaderIcon><IconCalendar /></CardHeaderIcon>
              <CardHeaderTitle>1. Select a Date</CardHeaderTitle>
              <CardHeaderRight>
                {dateStatus !== "done" && (
                  <Chevron $open={dateStatus === "open"}><polyline points="6 9 12 15 18 9" /></Chevron>
                )}
              </CardHeaderRight>
            </CardHeader>

            {dateStatus === "done" && (
              <SummaryRow>
                <SummaryValue>{formatDateLong(selectedDate)}</SummaryValue>
                <EditBtn type="button" onClick={() => setOpenStep(1)}>Edit</EditBtn>
              </SummaryRow>
            )}

            <CardBody $open={dateStatus === "open"}>
              <CalendarNav>
                <CalendarNavBtn type="button" onClick={handlePrevMonth} disabled={!canGoPrevMonth} aria-label="Previous month">
                  <IconChevronLeft />
                </CalendarNavBtn>
                <CalendarLabel>{formatMonthLabel(viewMonth)}</CalendarLabel>
                <CalendarNavBtn type="button" onClick={handleNextMonth} aria-label="Next month">
                  <IconChevronRight />
                </CalendarNavBtn>
              </CalendarNav>

              <WeekRow>
                {WEEKDAY_LABELS.map((d) => <WeekdayLabel key={d}>{d}</WeekdayLabel>)}
              </WeekRow>

              <DaysGrid>
                {monthMatrix.map(({ date, inMonth }, i) => {
                  const disabled = !inMonth || isPastDay(date, today);
                  const selected = sameDay(date, selectedDate);
                  return (
                    <DayCell
                      key={i}
                      type="button"
                      $inMonth={inMonth}
                      $disabled={disabled}
                      $selected={selected}
                      disabled={disabled}
                      onClick={() => handleSelectDate(date)}
                      aria-label={formatDateLong(date)}
                      aria-pressed={selected}
                    >
                      {date.getDate()}
                    </DayCell>
                  );
                })}
              </DaysGrid>

              <CalendarLegend>
                <LegendItem $dot="var(--c-accent)">Available</LegendItem>
                <LegendItem>Unavailable</LegendItem>
                <LegendItem>Past Date</LegendItem>
              </CalendarLegend>
            </CardBody>
          </Card>

          {/* ── 2. Select a Time ── */}
          <Card $area="time">
            <CardHeader
              type="button"
              onClick={() => handleHeaderClick(2, timeReachable)}
              disabled={!timeReachable}
              aria-expanded={timeStatus === "open"}
              aria-disabled={!timeReachable}
            >
              <CardHeaderIcon><IconClock /></CardHeaderIcon>
              <CardHeaderTitle $muted={!timeReachable}>2. Select a Time</CardHeaderTitle>
              <CardHeaderRight>
                {timeStatus !== "done" && (
                  <Chevron $open={timeStatus === "open"} $muted={!timeReachable}>
                    <polyline points="6 9 12 15 18 9" />
                  </Chevron>
                )}
              </CardHeaderRight>
            </CardHeader>

            {timeStatus === "done" && (
              <SummaryRow>
                <SummaryValue>{selectedTime}</SummaryValue>
                <EditBtn type="button" onClick={() => setOpenStep(2)}>Edit</EditBtn>
              </SummaryRow>
            )}

            <CardBody $open={timeStatus === "open"}>
              {selectedDate && (
                <DateChip><IconCalendar />{formatDateLong(selectedDate)}</DateChip>
              )}

              {TIME_GROUPS.map((group) => (
                <TimeGroup key={group.label}>
                  <TimeGroupLabel><group.icon />{group.label}</TimeGroupLabel>
                  <TimeSlotsGrid>
                    {group.slots.map((slot) => (
                      <TimeSlot
                        key={slot}
                        type="button"
                        $selected={selectedTime === slot}
                        onClick={() => handleSelectTime(slot)}
                        aria-pressed={selectedTime === slot}
                      >
                        {slot}
                      </TimeSlot>
                    ))}
                  </TimeSlotsGrid>
                </TimeGroup>
              ))}

              <TimezoneNote><IconLeaf />All times are in India Standard Time (IST)</TimezoneNote>
            </CardBody>
          </Card>

          {/* ── Your Selection (desktop) / Session summary (always) ── */}
          <AsideCard>
            <AsideHeading><IconCalendar />Your Selection</AsideHeading>
            <AsideRows>
              <AsideRow>
                <IconCalendar />
                <div>
                  <AsideRowLabel>Date</AsideRowLabel>
                  <AsideRowValue>{selectedDate ? formatDateLong(selectedDate) : "Not selected yet"}</AsideRowValue>
                </div>
              </AsideRow>
              <AsideRow>
                <IconClock />
                <div>
                  <AsideRowLabel>Time</AsideRowLabel>
                  <AsideRowValue>{selectedTime || "Not selected yet"}</AsideRowValue>
                </div>
              </AsideRow>
            </AsideRows>

            <SessionIcon><IconLotus /></SessionIcon>
            <SessionTitle>Yoga Shastra Session</SessionTitle>
            <SessionDesc>A personalized yoga and wellness experience for your well-being.</SessionDesc>
          </AsideCard>

          {/* ── 3. Your Details ── */}
          <Card $area="details">
            <CardHeader
              type="button"
              onClick={() => handleHeaderClick(3, detailsReachable)}
              disabled={!detailsReachable}
              aria-expanded={detailsStatus === "open"}
              aria-disabled={!detailsReachable}
            >
              <CardHeaderIcon><IconUser /></CardHeaderIcon>
              <CardHeaderTitle $muted={!detailsReachable}>3. Your Details</CardHeaderTitle>
              <CardHeaderRight>
                <Chevron $open={detailsStatus === "open"} $muted={!detailsReachable}>
                  <polyline points="6 9 12 15 18 9" />
                </Chevron>
              </CardHeaderRight>
            </CardHeader>

            <CardBody $open={detailsStatus === "open"}>
              <CardSubtitle>Please provide your details so we can reach you.</CardSubtitle>

              <form onSubmit={handleSubmit} noValidate>
                <FormGrid>
                  <Field>
                    <Label htmlFor="av-name">Full Name<em>*</em></Label>
                    <InputWrap $error={!!formErrors.name}>
                      <IconUser />
                      <Input
                        id="av-name"
                        type="text"
                        placeholder="Enter your full name"
                        value={form.name}
                        onChange={(e) => updateField("name", e.target.value)}
                        autoComplete="name"
                      />
                    </InputWrap>
                    {formErrors.name && <ErrorText>{formErrors.name}</ErrorText>}
                  </Field>

                  <Field>
                    <Label htmlFor="av-mobile">Mobile Number<em>*</em></Label>
                    <InputWrap $error={!!formErrors.mobile}>
                      <IconPhone />
                      <CountryCode
                        aria-label="Country code"
                        value={form.countryCode}
                        onChange={(e) => updateField("countryCode", e.target.value)}
                      >
                        <option value="+91">+91</option>
                        <option value="+1">+1</option>
                        <option value="+44">+44</option>
                        <option value="+971">+971</option>
                      </CountryCode>
                      <Input
                        id="av-mobile"
                        type="tel"
                        inputMode="numeric"
                        placeholder="Enter mobile number"
                        value={form.mobile}
                        onChange={(e) => updateField("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
                        autoComplete="tel-national"
                      />
                    </InputWrap>
                    {formErrors.mobile && <ErrorText>{formErrors.mobile}</ErrorText>}
                  </Field>

                  <Field>
                    <Label htmlFor="av-email">Email Address (Optional)</Label>
                    <InputWrap $error={!!formErrors.email}>
                      <IconMail />
                      <Input
                        id="av-email"
                        type="email"
                        placeholder="Enter your email address"
                        value={form.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        autoComplete="email"
                      />
                    </InputWrap>
                    {formErrors.email && <ErrorText>{formErrors.email}</ErrorText>}
                  </Field>
                </FormGrid>

                <PrivacyNote>
                  <IconLock />
                  Your information is safe with us and will only be used to confirm your availability.
                </PrivacyNote>

                <SubmitRow>
                  <SubmitBtn type="submit" disabled={submitting}>
                    {submitting ? "Checking…" : "Check Availability"}
                    {!submitting && <IconArrowRight />}
                  </SubmitBtn>
                  <SubmitHint>You will receive a confirmation on your mobile number.</SubmitHint>
                  {status && <FormStatus $variant={status.variant}>{status.message}</FormStatus>}
                </SubmitRow>
              </form>
            </CardBody>
          </Card>
        </GridWrap>

        <StepCaption>
          <strong>STEP {openStep}</strong>
          <span>{STEP_LABELS[openStep]}</span>
        </StepCaption>

        <FeatureGrid>
          {FEATURES.map((f, i) => (
            <FeatureItem key={f.title} $index={i}>
              <FeatureIcon><f.icon /></FeatureIcon>
              <FeatureTitle>{f.title}</FeatureTitle>
              <FeatureDesc>{f.desc}</FeatureDesc>
            </FeatureItem>
          ))}
        </FeatureGrid>

        <NoteBar>
          <IconLeaf />
          <span><strong>Note:</strong> You will receive a confirmation on your mobile number.</span>
        </NoteBar>
      </Inner>
    </Section>
  );
};

Availability.displayName = "Availability";
export default React.memo(Availability);